import React from "react";
import { formatPatientInitials } from "../formatUtils";
import {
  AgencyId,
  CaregiverVisitBroadcastEngagementDetails,
  EngagementStatus,
  PublicVisitBroadcastDetailsTypes,
} from "../messages";
import { VisitBroadcastId, VisitInstanceId } from "../messages/ObjectIds";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { HashSet } from "../utils/HashSet";
import { AgencyDetailsCard } from "./AgencyDetailsCard";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableVisitDatesAppCard from "./AvailableVisitDatesAppCard";
import AvailableVisitDetails from "./AvailableVisitDetails";
import AvailableVisitFooter from "./AvailableVisitFooter";
import AvailableVisitGeneralNotes from "./AvailableVisitGeneralNotes";
import BadgesGroup from "./BadgesGroup";
import { If } from "./Generic/If";
import { MapWidget } from "./MapWidget";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";
import VisitChatIcon from "./VisitChatIcon";
import VisitInfoCards from "./VisitInfoCards";
import VisitStaffingPreferencesBadges from "./VisitStaffingPreferencesBadges";
import { AgenciesBlock } from "./AgenciesList/AgenciesListUtils";
import { List } from "../utils/List";

interface Props {
  visit: {
    type: "Available";
    id: VisitBroadcastId;
    publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.Pattern;
    caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
  };
  caregiver: AppCaregiver;
  activeAgencies: List<AgenciesBlock>;
  coordinator: AgencyMember;
  engagementStatus: EngagementStatus;
  countNewPatientChatMessages: number;
  isPopperOpen: boolean;
  checkedVisits: HashSet<VisitInstanceId>;
  checkedPatterns: HashSet<string>;
  areAllShiftsChecked: boolean;
  onClickLocation: () => void;
  onClickGoBack: () => void;
  onClickChat: () => void;
  onClickAvailableVisitCallAgency?: (
    agencyId: AgencyId,
    visitId: VisitBroadcastId,
    phoneNumber: string | null
  ) => Promise<void>;
  onClickOnboardingVisitCallAgency?: () => void;
  onClickRequest: (
    checkedVisits: HashSet<VisitInstanceId>,
    checkedPatterns: HashSet<string>
  ) => void;
  onClickReject: () => void;
  onClickCancel: () => void;
  onCheckVisit: (visitData: CheckedVisitData) => void;
  onClickCall: () => void;
}

const AvailableVisitScreenPattern = (props: Props) => {
  const isMedflytCaregiver = props.caregiver.isCaregiverOfMedFlyt;
  const activeAgenciesCount =
    props.activeAgencies
      .filter((a) => a.status === "ACTIVE")
      .first()
      ?.agencies.length() ?? 0;
  const isMedfytCaregiverWithOtherActiveAgencies = isMedflytCaregiver && activeAgenciesCount > 1;
  const chatAction = (
    <VisitChatIcon
      badge={props.countNewPatientChatMessages}
      isPopperOpen={props.isPopperOpen}
      onClick={props.onClickChat}
    />
  );

  const isRequestPermitted = (() => {
    return (
      props.checkedVisits.size() > 0 ||
      props.checkedPatterns.size() > 0 ||
      props.visit.publicVisitBroadcastDetails.visitInstances.length === 1
    );
  })();

  return (
    <Scaffold
      title={
        <ScaffoldTitle
          label={getLocationDisplayLocation(props.visit.publicVisitBroadcastDetails.location)}
        />
      }
      footer={
        <AvailableVisitFooter
          status={props.engagementStatus}
          onClickRequest={() => props.onClickRequest(props.checkedVisits, props.checkedPatterns)}
          onClickReject={props.onClickReject}
          onClickCancel={props.onClickCancel}
          isRequestPermitted={isRequestPermitted}
          isSingleShift={false}
        />
      }
      endActions={chatAction}
      onClickGoBack={props.onClickGoBack}
      className="AvailableVisitScreen"
      overlayHeader={true}
    >
      <MapWidget
        origin={props.caregiver.addressGeoLocation}
        destination={props.visit.publicVisitBroadcastDetails.location.location}
        patientInitials={formatPatientInitials(
          props.visit.publicVisitBroadcastDetails.patientInitials
        )}
        onOpenMapClick={props.onClickLocation}
      />

      <BadgesGroup labels={props.visit.publicVisitBroadcastDetails.visitLabels} />

      <If condition={props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}>
        <VisitStaffingPreferencesBadges
          staffingPreferences={props.visit.publicVisitBroadcastDetails.staffingPreferences2}
        />
      </If>

      <AvailableVisitDatesAppCard
        broadcastDetails={props.visit.publicVisitBroadcastDetails}
        checkedVisits={props.checkedVisits}
        checkedPatterns={props.checkedPatterns}
        requestedVisitBatch={props.visit.caregiverVisitEngagementDetails.requested}
        areAllShiftsChecked={props.areAllShiftsChecked}
        visitType={props.visit.type}
        flexibleVisitProps={null}
        onCheckVisit={props.onCheckVisit}
      />

      <AvailableVisitDetails
        patientName={props.visit.publicVisitBroadcastDetails.patientFirstName}
        patientAge={props.visit.publicVisitBroadcastDetails.patientAge}
        patientGender={props.visit.publicVisitBroadcastDetails.patientGender}
        patientLanguages={props.visit.publicVisitBroadcastDetails.patientLanguages}
        broadcastDetails={props.visit.publicVisitBroadcastDetails}
        caregiverLocation={props.caregiver.addressGeoLocation}
        onClickLocation={props.onClickLocation}
        checkedVisits={props.checkedVisits}
        onCheckVisit={props.onCheckVisit}
        checkedPatterns={props.checkedPatterns}
        requestedVisitBatch={props.visit.caregiverVisitEngagementDetails.requested}
        areAllShiftsChecked={props.areAllShiftsChecked}
        visitType={props.visit.type}
        isMedflytCaregiver={props.caregiver.isCaregiverOfMedFlyt}
      />

      <If condition={props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}>
        <VisitInfoCards
          patientRequests={props.visit.publicVisitBroadcastDetails.patientRequests}
          patientTasks={props.visit.publicVisitBroadcastDetails.patientTasks}
          patientMedicalNotes={props.visit.publicVisitBroadcastDetails.patientMedicalNotes}
          patientAllergies={props.visit.publicVisitBroadcastDetails.patientAllergies}
        />
      </If>

      <AvailableVisitGeneralNotes notes={props.visit.publicVisitBroadcastDetails.generalNotes} />
      {(isMedfytCaregiverWithOtherActiveAgencies || !isMedflytCaregiver) && (
        <AgencyDetailsCard
          agency={props.visit.publicVisitBroadcastDetails.agency}
          coordinatorInCharge={props.coordinator}
          onClickChat={props.onClickChat}
          onClickCall={props.onClickCall}
          showCallButton={!props.visit.publicVisitBroadcastDetails.agency.isMedflytAgency}
        />
      )}
    </Scaffold>
  );
};

export default AvailableVisitScreenPattern;
